import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Drawer,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Slider,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  ArrowForwardIosRounded,
  CloseRounded,
  FilterAltOutlined,
  LocationOnOutlined,
  TuneRounded,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import config from "../../Config";

const Category = () => {
  const navigate = useNavigate();
  const { mainCat, subCat, subId } = useParams();
  const isMobile = useMediaQuery("(max-width:900px)");

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const [size, setSize] = useState("");
  const [price, setPrice] = useState([0, 100]);

  useEffect(() => {
    let mounted = true;

    const fetchProducts = async () => {
      if (!subId) {
        setProducts([]);
        setPrice([0, 100]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const response = await axios.get(
          `${config.apiBaseUrl}/${config.subCategory}`,
          {
            headers: {
              "sub-id": subId,
            },
          },
        );

        if (!mounted) {
          return;
        }

        const apiProducts = Array.isArray(response?.data?.Products)
          ? response.data.Products
          : [];

        setProducts(apiProducts);

        const prices = apiProducts
          .map((item) => Number(item?.product?.rentAmount))
          .filter((value) => Number.isFinite(value) && value >= 0);

        const maxPrice = prices.length ? Math.max(...prices) : 100;

        setPrice([0, Math.max(maxPrice, 100)]);
        setSize("");
      } catch (error) {
        if (!mounted) {
          return;
        }

        console.error("Error fetching category products:", error);
        setProducts([]);
        setPrice([0, 100]);
        setSize("");
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchProducts();

    return () => {
      mounted = false;
    };
  }, [subId]);

  const sizeOptions = useMemo(() => {
    const sizes = products
      .map((item) => item?.product?.size)
      .filter(Boolean)
      .map((item) => String(item).trim());

    return [...new Set(sizes)];
  }, [products]);

  const maxProductPrice = useMemo(() => {
    const prices = products
      .map((item) => Number(item?.product?.rentAmount))
      .filter((value) => Number.isFinite(value) && value >= 0);

    return prices.length ? Math.max(...prices) : 100;
  }, [products]);

  const sliderMax = Math.max(maxProductPrice, 100);

  const filteredProducts = useMemo(() => {
    const minPrice = Number(price[0]) || 0;
    const maxPrice = Number(price[1]) || sliderMax;

    return products.filter((item) => {
      const product = item?.product || {};

      const productSize = String(product?.size || "").trim();
      const rentAmount = Number(product?.rentAmount || 0);

      const matchesSize =
        !size || productSize.toLowerCase() === size.toLowerCase();

      const matchesPrice = rentAmount >= minPrice && rentAmount <= maxPrice;

      return matchesSize && matchesPrice;
    });
  }, [products, size, price, sliderMax]);

  const activeFilterCount = [
    Boolean(size),
    price[0] > 0,
    price[1] < sliderMax,
  ].filter(Boolean).length;

  const handlePriceChange = (_, value) => {
    if (!Array.isArray(value)) {
      return;
    }

    const nextMin = Number(value[0]);
    const nextMax = Number(value[1]);

    if (!Number.isFinite(nextMin) || !Number.isFinite(nextMax)) {
      return;
    }

    setPrice([
      Math.max(0, Math.min(nextMin, sliderMax)),
      Math.max(0, Math.min(nextMax, sliderMax)),
    ]);
  };

  const clearFilters = () => {
    setSize("");
    setPrice([0, sliderMax]);
  };

  const handleProductClick = (productId) => {
    if (!productId) {
      return;
    }

    navigate(`/productdetail/${mainCat}/${subCat}/${productId}`);
  };

  const renderFilterContent = () => (
    <Stack spacing={3}>
      <Box>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "#77716a",
            mb: 1.5,
          }}
        >
          Size
        </Typography>

        <FormControl fullWidth size="small">
          <Select
            value={size}
            displayEmpty
            onChange={(event) => setSize(event.target.value)}
            sx={{
              background: "#fff",
              borderRadius: 0,
              fontSize: 13,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#d8d1c7",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#977743",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#977743",
              },
            }}
          >
            <MenuItem value="">All Sizes</MenuItem>

            {sizeOptions.map((item) => (
              <MenuItem value={item} key={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "#77716a",
            mb: 1.5,
          }}
        >
          Rental Price
        </Typography>

        <Slider
          key={`price-slider-${sliderMax}`}
          value={[Math.min(price[0], sliderMax), Math.min(price[1], sliderMax)]}
          min={0}
          max={sliderMax}
          step={1}
          disableSwap
          onChange={handlePriceChange}
          valueLabelDisplay="auto"
          valueLabelFormat={(value) =>
            `₹${Number(value).toLocaleString("en-IN")}`
          }
          sx={{
            color: "#977743",
            "& .MuiSlider-thumb": {
              width: 15,
              height: 15,
              background: "#171717",
              border: "2px solid #f7f4ef",
              "&:hover, &.Mui-focusVisible": {
                boxShadow: "0 0 0 6px rgba(151,119,67,0.14)",
              },
            },
            "& .MuiSlider-track": {
              height: 2,
            },
            "& .MuiSlider-rail": {
              height: 2,
              background: "#d4cec5",
            },
          }}
        />

        <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
          <Typography
            sx={{
              fontSize: 11,
              color: "#77716a",
            }}
          >
            ₹{Number(price[0]).toLocaleString("en-IN")}
          </Typography>

          <Typography
            sx={{
              fontSize: 11,
              color: "#77716a",
            }}
          >
            ₹{Number(price[1]).toLocaleString("en-IN")}
          </Typography>
        </Stack>
      </Box>

      {activeFilterCount > 0 && (
        <Button
          variant="outlined"
          onClick={clearFilters}
          sx={{
            minHeight: 46,
            borderRadius: 0,
            borderColor: "#171717",
            color: "#171717",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            "&:hover": {
              borderColor: "#977743",
              background: "#f1ede7",
            },
          }}
        >
          Clear Filters
        </Button>
      )}
    </Stack>
  );

  const renderProductCard = (item, index) => {
    const product = item?.product || {};

    const productId = product?.productId;
    const productName = product?.title || "Untitled Collection";
    const image = item?.productImage?.[0] || "";
    const rentAmount = Number(product?.rentAmount || 0);
    const sizeValue = product?.size || "";
    const city = item?.address?.city || "India";
    const availableDate = item?.slots?.availableDate || "";

    return (
      <Box
        key={productId || item?._id || index}
        onClick={() => handleProductClick(productId)}
        sx={{
          cursor: productId ? "pointer" : "default",
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            position: "relative",
            aspectRatio: "0.78",
            overflow: "hidden",
            background: "#e9e4dc",
            "& img": {
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transition: "transform 0.7s cubic-bezier(.2,.7,.2,1)",
            },
            "&:hover img": {
              transform: "scale(1.045)",
            },
          }}
        >
          {image ? (
            <Box
              component="img"
              src={image}
              alt={productName}
              loading="lazy"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{
                width: "100%",
                height: "100%",
                color: "#8d867c",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Georgia, serif",
                  fontSize: 26,
                }}
              >
                HW
              </Typography>
            </Stack>
          )}

          <Box
            sx={{
              position: "absolute",
              top: 14,
              left: 14,
              px: 1.5,
              py: 0.7,
              background: "rgba(247,244,239,0.94)",
              backdropFilter: "blur(8px)",
            }}
          >
            <Typography
              sx={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
              }}
            >
              Rental
            </Typography>
          </Box>

          {sizeValue && (
            <Box
              sx={{
                position: "absolute",
                bottom: 14,
                left: 14,
                px: 1.3,
                py: 0.6,
                background: "rgba(247,244,239,0.94)",
                backdropFilter: "blur(8px)",
              }}
            >
              <Typography
                sx={{
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                }}
              >
                Size {sizeValue}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              position: "absolute",
              right: 14,
              bottom: 14,
              width: 42,
              height: 42,
              borderRadius: "50%",
              background: "#171717",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.3s ease",
            }}
          >
            <ArrowForwardIosRounded sx={{ fontSize: 12 }} />
          </Box>
        </Box>

        <Box
          sx={{
            pt: 2,
            pb: 1,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Georgia, serif",
              fontSize: {
                xs: 16,
                md: 19,
              },
              lineHeight: 1.25,
              color: "#171717",
              mb: 1,
              textTransform: "capitalize",
            }}
          >
            {productName}
          </Typography>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
          >
            <Typography
              sx={{
                minWidth: 0,
                fontSize: 10,
                color: "#77716a",
                letterSpacing: "0.04em",
                display: "flex",
                alignItems: "center",
                gap: 0.35,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <LocationOnOutlined sx={{ fontSize: 13 }} />
              {city}
            </Typography>

            <Typography
              sx={{
                flexShrink: 0,
                fontSize: 12,
                fontWeight: 700,
                color: "#977743",
              }}
            >
              ₹{rentAmount.toLocaleString("en-IN")}/day
            </Typography>
          </Stack>

          {availableDate && (
            <Typography
              sx={{
                mt: 0.8,
                fontSize: 10,
                color: "#8a857d",
                letterSpacing: "0.03em",
                lineHeight: 1.5,
              }}
            >
              Available {availableDate}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Navbar />

      <Box
        sx={{
          background: "#f7f4ef",
          minHeight: "100vh",
          color: "#171717",
        }}
      >
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            borderBottom: "1px solid #ddd7ce",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: 240,
              height: 240,
              border: "1px solid rgba(151,119,67,0.2)",
              borderRadius: "50%",
              right: -80,
              top: -120,
            }}
          />

          <Box
            sx={{
              position: "absolute",
              width: 120,
              height: 120,
              border: "1px solid rgba(151,119,67,0.16)",
              borderRadius: "50%",
              right: 80,
              bottom: -80,
            }}
          />

          <Container
            maxWidth="xl"
            sx={{
              py: {
                xs: 6,
                sm: 8,
                md: 10,
              },
            }}
          >
            <Stack
              direction={{
                xs: "column",
                md: "row",
              }}
              justifyContent="space-between"
              alignItems={{
                xs: "flex-start",
                md: "flex-end",
              }}
              spacing={3}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.24em",
                    textTransform: "uppercase",
                    color: "#977743",
                    mb: 2,
                  }}
                >
                  Hindustan Wardrobe
                </Typography>

                <Typography
                  component="h1"
                  sx={{
                    fontFamily: "Georgia, serif",
                    fontSize: {
                      xs: 42,
                      sm: 55,
                      md: 72,
                    },
                    fontWeight: 400,
                    lineHeight: 0.98,
                    letterSpacing: "-0.045em",
                    maxWidth: 800,
                    textTransform: "capitalize",
                  }}
                >
                  {subCat ? subCat.replace(/-/g, " ") : "The Collection"}
                </Typography>

                <Box
                  sx={{
                    width: 55,
                    height: 1,
                    background: "#977743",
                    mt: 3,
                    mb: 2.5,
                  }}
                />

                <Typography
                  sx={{
                    maxWidth: 560,
                    color: "#716d67",
                    fontSize: {
                      xs: 14,
                      md: 15,
                    },
                    lineHeight: 1.8,
                  }}
                >
                  Discover carefully selected pieces designed to make every
                  occasion feel extraordinary.
                </Typography>
              </Box>

              {!loading && products.length > 0 && (
                <Typography
                  sx={{
                    fontSize: 10,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: "#77716a",
                  }}
                >
                  {filteredProducts.length}{" "}
                  {filteredProducts.length === 1 ? "Piece" : "Pieces"}
                </Typography>
              )}
            </Stack>
          </Container>
        </Box>

        <Container
          maxWidth="xl"
          sx={{
            py: {
              xs: 3,
              md: 5,
            },
          }}
        >
          {!loading && products.length > 0 && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                display: {
                  xs: "flex",
                  md: "none",
                },
                mb: 3,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{
                  maxWidth: "75%",
                }}
              >
                {size && (
                  <Chip
                    label={`Size ${size}`}
                    onDelete={() => setSize("")}
                    size="small"
                    sx={{
                      background: "#fff",
                      borderRadius: 0,
                      fontSize: 11,
                    }}
                  />
                )}

                {activeFilterCount > 0 && !size && (
                  <Chip
                    label={`${activeFilterCount} filter${
                      activeFilterCount > 1 ? "s" : ""
                    }`}
                    size="small"
                    sx={{
                      background: "#fff",
                      borderRadius: 0,
                      fontSize: 11,
                    }}
                  />
                )}
              </Stack>

              <Button
                onClick={() => setMobileFilterOpen(true)}
                startIcon={<TuneRounded />}
                sx={{
                  minWidth: "auto",
                  flexShrink: 0,
                  color: "#171717",
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                }}
              >
                Filter
              </Button>
            </Stack>
          )}

          {loading ? (
            <Box
              sx={{
                minHeight: 500,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Stack alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    border: "1px solid #c9c1b6",
                    borderTopColor: "#171717",
                    borderRadius: "50%",
                    animation: "categorySpin 1s linear infinite",
                    "@keyframes categorySpin": {
                      to: {
                        transform: "rotate(360deg)",
                      },
                    },
                  }}
                />

                <Typography
                  sx={{
                    fontFamily: "Georgia, serif",
                    fontSize: 19,
                  }}
                >
                  Curating your wardrobe...
                </Typography>
              </Stack>
            </Box>
          ) : products.length === 0 ? (
            <Box
              sx={{
                minHeight: {
                  xs: 500,
                  md: 580,
                },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                position: "relative",
                overflow: "hidden",
                borderTop: "1px solid #ddd7ce",
                borderBottom: "1px solid #ddd7ce",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  width: 260,
                  height: 260,
                  border: "1px solid rgba(151,119,67,0.18)",
                  borderRadius: "50%",
                  left: -100,
                  top: -90,
                }}
              />

              <Box
                sx={{
                  position: "absolute",
                  width: 170,
                  height: 170,
                  border: "1px solid rgba(151,119,67,0.14)",
                  borderRadius: "50%",
                  right: -45,
                  bottom: -65,
                }}
              />

              <Stack
                alignItems="center"
                sx={{
                  position: "relative",
                  maxWidth: 620,
                  px: 3,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.24em",
                    textTransform: "uppercase",
                    color: "#977743",
                    mb: 2,
                  }}
                >
                  Collection Update
                </Typography>

                <Typography
                  sx={{
                    fontFamily: "Georgia, serif",
                    fontSize: {
                      xs: 40,
                      md: 58,
                    },
                    fontWeight: 400,
                    lineHeight: 1.05,
                    letterSpacing: "-0.03em",
                    mb: 2,
                  }}
                >
                  Nothing available here
                </Typography>

                <Box
                  sx={{
                    width: 50,
                    height: 1,
                    background: "#977743",
                    mb: 3,
                  }}
                />

                <Typography
                  sx={{
                    color: "#77716a",
                    fontSize: 14,
                    lineHeight: 1.8,
                    maxWidth: 500,
                    mb: 4,
                  }}
                >
                  We couldn't find any pieces in this collection right now.
                  Explore our other collections to discover something you'll
                  love.
                </Typography>

                <Button
                  onClick={() => navigate("/")}
                  endIcon={<ArrowForwardIosRounded sx={{ fontSize: 12 }} />}
                  sx={{
                    minHeight: 52,
                    px: 4,
                    background: "#171717",
                    color: "#fff",
                    borderRadius: 0,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    "&:hover": {
                      background: "#977743",
                    },
                  }}
                >
                  Explore Other Collections
                </Button>
              </Stack>
            </Box>
          ) : filteredProducts.length === 0 ? (
            <Box
              sx={{
                minHeight: {
                  xs: 460,
                  md: 520,
                },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                borderTop: "1px solid #ddd7ce",
                borderBottom: "1px solid #ddd7ce",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Stack
                alignItems="center"
                sx={{
                  maxWidth: 600,
                  px: 3,
                  position: "relative",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.22em",
                    textTransform: "uppercase",
                    color: "#977743",
                    mb: 2,
                  }}
                >
                  Refine Your Search
                </Typography>

                <Typography
                  sx={{
                    fontFamily: "Georgia, serif",
                    fontSize: {
                      xs: 36,
                      md: 52,
                    },
                    lineHeight: 1.05,
                    letterSpacing: "-0.03em",
                    mb: 2,
                  }}
                >
                  No pieces match your filters
                </Typography>

                <Box
                  sx={{
                    width: 50,
                    height: 1,
                    background: "#977743",
                    mb: 3,
                  }}
                />

                <Typography
                  sx={{
                    color: "#77716a",
                    fontSize: 14,
                    lineHeight: 1.8,
                    mb: 4,
                  }}
                >
                  Try adjusting your filters to discover more pieces from this
                  collection.
                </Typography>

                <Button
                  onClick={clearFilters}
                  startIcon={<FilterAltOutlined />}
                  sx={{
                    minHeight: 52,
                    px: 4,
                    background: "#171717",
                    color: "#fff",
                    borderRadius: 0,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    "&:hover": {
                      background: "#977743",
                    },
                  }}
                >
                  Clear Filters
                </Button>
              </Stack>
            </Box>
          ) : (
            <Stack
              direction={{
                xs: "column",
                md: "row",
              }}
              spacing={{
                xs: 4,
                md: 5,
              }}
              alignItems="flex-start"
            >
              <Box
                sx={{
                  width: {
                    xs: "100%",
                    md: 240,
                  },
                  flexShrink: 0,
                  display: {
                    xs: "none",
                    md: "block",
                  },
                }}
              >
                <Box
                  sx={{
                    position: "sticky",
                    top: 100,
                    borderTop: "1px solid #171717",
                    pt: 2.5,
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 3 }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <FilterAltOutlined
                        sx={{
                          fontSize: 17,
                          color: "#977743",
                        }}
                      />

                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: "0.16em",
                          textTransform: "uppercase",
                        }}
                      >
                        Filters
                      </Typography>
                    </Stack>

                    {activeFilterCount > 0 && (
                      <Typography
                        sx={{
                          fontSize: 10,
                          color: "#977743",
                        }}
                      >
                        {activeFilterCount} active
                      </Typography>
                    )}
                  </Stack>

                  {renderFilterContent()}
                </Box>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  width: "100%",
                  minWidth: 0,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    mb: 3,
                    pb: 2,
                    borderBottom: "1px solid #ddd7ce",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 10,
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      color: "#77716a",
                    }}
                  >
                    Showing {filteredProducts.length} of {products.length}{" "}
                    pieces
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(2, minmax(0, 1fr))",
                      sm: "repeat(2, minmax(0, 1fr))",
                      lg: "repeat(3, minmax(0, 1fr))",
                      xl: "repeat(4, minmax(0, 1fr))",
                    },
                    gap: {
                      xs: 2,
                      sm: 3,
                      md: 3.5,
                    },
                  }}
                >
                  {filteredProducts.map(renderProductCard)}
                </Box>
              </Box>
            </Stack>
          )}
        </Container>
      </Box>

      <Footer />

      {isMobile && (
        <Drawer
          anchor="right"
          open={mobileFilterOpen}
          onClose={() => setMobileFilterOpen(false)}
          keepMounted={false}
          PaperProps={{
            sx: {
              width: {
                xs: "88%",
                sm: 380,
              },
              maxWidth: "100%",
              background: "#f7f4ef",
              p: 3,
            },
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              mb: 4,
              pb: 2,
              borderBottom: "1px solid #d8d1c7",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <FilterAltOutlined
                sx={{
                  color: "#977743",
                }}
              />

              <Typography
                sx={{
                  fontFamily: "Georgia, serif",
                  fontSize: 24,
                }}
              >
                Filters
              </Typography>
            </Stack>

            <IconButton
              onClick={() => setMobileFilterOpen(false)}
              sx={{
                color: "#171717",
              }}
            >
              <CloseRounded />
            </IconButton>
          </Stack>

          {renderFilterContent()}

          <Button
            onClick={() => setMobileFilterOpen(false)}
            startIcon={<TuneRounded />}
            sx={{
              mt: 4,
              minHeight: 52,
              background: "#171717",
              color: "#fff",
              borderRadius: 0,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              "&:hover": {
                background: "#977743",
              },
            }}
          >
            View {filteredProducts.length} Pieces
          </Button>
        </Drawer>
      )}
    </>
  );
};

export default Category;
